<script>
import { v4 as uuid } from "uuid";
import Select from "../Components/Select.vue";
import DatePicker from "../Components/DatePicker.vue";
import Travelers from "../Components/FlightTravelers.vue";
import { BreakPoints, externalFlightRoutes } from "../../../Constants";
import { flightLocationMapper } from "../../../Mappers";
import { checkDateDiff } from "../../../Helpers/tools";

export default {
  components: {
    Select,
    DatePicker,
    Travelers,
  },

  data() {
    return {
      externalFlightRoutes,
      activeSection: externalFlightRoutes[0].value,
      locationSearchUrl: null,
      defaultParams: {
        originPlace: true,
      },
      submitData: {
        routes: [],
        travelers: null,
        cabinType: null,
      },
      defaultCityList: [],
      isLoading: false,
    };
  },

  computed: {
    isRoundTrip() {
      return this.activeSection === this.externalFlightRoutes[1].value;
    },

    isMultiCity() {
      return this.activeSection === this.externalFlightRoutes[2].value;
    },

    dateDiff() {
      const { departureDate, returnDate } = this.submitData.routes[0];

      return departureDate && returnDate
        ? checkDateDiff({ firstDate: returnDate, secondDate: departureDate })
        : null;
    },

    isFormValid() {
      return Object.values(this.$refs).every((ref) => {
        if (Array.isArray(ref)) {
          return ref.every((instance) => instance.isValid);
        }
        return ref?.isValid;
      });
    },
  },

  methods: {
    flightLocationMapper,

    fillNextInput(type, index) {
      if (window.innerWidth <= BreakPoints.md) return;
      const inputOrder = ["origin", "destination", "datespicker"];
      // Handle the case when origin and destination are the same
      if (type === "origin" || type === "destination") {
        const otherType = type === "origin" ? "destination" : "origin";
        if (
          this.submitData.routes[index][type]?.code?.toLowerCase() ===
          this.submitData.routes[index][otherType]?.code?.toLowerCase()
        ) {
          this.$refs[otherType][index].handleResetValue();
          this.$refs[otherType][index].handleOpen();
          return;
        }
      }
      // Find the next empty input
      const nextInput = inputOrder.find(
        (input) => !this.submitData.routes[index][input]
      );

      if (nextInput) {
        this.$refs[nextInput]?.[index]?.handleOpen();
      }
    },

    handleReverseLocation(index) {
      const { origin, destination } = this.submitData.routes[index];

      this.$refs.origin[index].handleSelectItem(destination);
      this.$refs.destination[index].handleSelectItem(origin);
    },

    validateAllInputs() {
      Object.values(this.$refs).forEach((ref) => {
        if (Array.isArray(ref)) {
          ref.forEach((instance) => instance.validate());
        } else {
          ref?.validate();
        }
      });
    },

    buildSearchUrl() {
      const { routes, travelers, cabinType } = this.submitData;
      const { externalFlightSearchUrl } = window.bookingSectionInitialize;
      let searchUrl = `${externalFlightSearchUrl}/`;

      routes.forEach((route, index) => {
        searchUrl += `${route.origin.code}-${route.destination.code}${
          index !== routes.length - 1 ? "-" : ""
        }`;
      });

      searchUrl += `/?`;

      routes.forEach((route) => {
        searchUrl += `dep=${route.departureDate}&`;
      });

      searchUrl += `&cab=${cabinType.cabinTypeNumber}&adt=${travelers.adult}&chd=${travelers.child}&inf=${travelers.infant}`;

      if (this.isRoundTrip) searchUrl += `&ret=${routes[0].returnDate}`;

      return searchUrl;
    },

    async handleSearch() {
      this.validateAllInputs();

      if (this.isFormValid) {
        this.isLoading = true;

        try {
          const searchUrl = this.buildSearchUrl();
          window.location.assign(searchUrl);
        } catch (error) {
          console.error("Error during search:", error);
          this.isLoading = false;
        }
      }
    },

    handleAddRoute() {
      this.submitData.routes.push({
        origin: null,
        destination: null,
        departureDate: null,
        key: uuid(),
      });
    },

    handleRemoveRoute(route) {
      this.submitData.routes = this.submitData.routes.filter(
        (r) => r.key !== route.key
      );
    },

    handleSearchMode() {
      if (!this.isMultiCity) {
        this.submitData.routes = this.submitData.routes.splice(0, 1);
      }
    },

    datepickerValueHandler(index, val) {
      if (this.isRoundTrip) {
        this.submitData.routes[index].departureDate = val[0];
        this.submitData.routes[index].returnDate = val[1];
      } else this.submitData.routes[index].departureDate = val;
    },
  },

  created() {
    if (window.externalCityList?.length) {
      this.defaultCityList = this.flightLocationMapper({
        model: {
          results: window.externalCityList,
        },
      });
    }

    this.locationSearchUrl =
      window.bookingSectionInitialize.externalFlightLocationSearchUrl;

    this.handleAddRoute();
  },
};
</script>

<template>
  <div>
    <ul class="booking__type">
      <li
        class="booking__type-item"
        v-for="item in externalFlightRoutes"
        :key="item.key"
      >
        <input
          type="radio"
          class="checkmark"
          v-model="activeSection"
          :id="item.key"
          :value="item.value"
          autocomplete="off"
          name="external-flight-active-section"
          @change="handleSearchMode"
        />

        <label :for="item.key"> {{ item.title }}</label>
      </li>
    </ul>

    <div class="booking__main">
      <div
        :class="['booking__row', { 'booking__row--no-border': isMultiCity }]"
        v-for="(route, index) in submitData.routes"
        :key="route.key"
        v-memo="[route.key, isMultiCity]"
      >
        <div
          :class="['booking__items', { 'booking__items--border': isMultiCity }]"
        >
          <div
            :class="[
              'booking__location',
              isMultiCity ? 'booking__location--xxl' : 'booking__location--xl',
            ]"
          >
            <button
              class="booking__location-reverse icon-change"
              @click="handleReverseLocation(index)"
            />

            <Select
              label="از"
              placeholder="انتخاب مبدا"
              name="origin"
              v-model="submitData.routes[index].origin"
              :mapper="flightLocationMapper"
              :default-list="defaultCityList"
              :get-data-url="locationSearchUrl"
              :default-params="defaultParams"
              item-title="cityTitle"
              item-value="code"
              item-icon="icon"
              search-key="keyword"
              @fill-next-input="() => fillNextInput('origin', index)"
              ref="origin"
              list-header="شهرهای پرطرفدار"
            />

            <Select
              label="به"
              placeholder="انتخاب مقصد"
              name="destination"
              v-model="submitData.routes[index].destination"
              :mapper="flightLocationMapper"
              :default-list="defaultCityList"
              :get-data-url="locationSearchUrl"
              :default-params="defaultParams"
              item-title="cityTitle"
              item-value="code"
              item-icon="icon"
              search-key="keyword"
              @fill-next-input="() => fillNextInput('destination', index)"
              ref="destination"
              list-header="شهرهای پرطرفدار"
            />
          </div>

          <DatePicker
            label="تاریخ رفت"
            placeholder="انتخاب تاریخ رفت"
            :errorLabel="isRoundTrip?'تاریخ رفت و برگشت':'تاریخ رفت'"
            :value="
              isRoundTrip
                ? [
                    submitData.routes[index].departureDate,
                    submitData.routes[index].returnDate,
                  ]
                : submitData.routes[index].departureDate
            "
            @input="(val) => datepickerValueHandler(index, val)"
            @fill-next-input="() => fillNextInput('datespicker', index)"
            ref="datespicker"
            name="departureDate"
            :selectionMode="isRoundTrip ? 'range' : 'single'"
            :showSecondDate="true"
            secondLabel="تاریخ برگشت"
            secondPlaceholder="انتخاب تاریخ برگشت"
          />

          <div v-if="!isMultiCity" class="booking__travelers">
            <Travelers
              v-model="submitData.travelers"
              :cabinType.sync="submitData.cabinType"
              ref="travelers"
            />
          </div>
        </div>

        <div v-if="isMultiCity" class="booking__row-header fa-font pr-md--1">
          <span class="booking__row-header-counter bold">
            <span class="hide-in-desktop">مسیر </span>
            <span>{{ index + 1 }}</span>
          </span>

          <button
            v-if="submitData.routes.length > 1"
            @click="handleRemoveRoute(route)"
            class="booking__row-header-button icon-close"
          />
        </div>

        <button
          v-else
          @click="handleSearch"
          class="booking__submit btn btn-red"
          title="جستجو"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="bounce-loading bounce-loading--white">
            <span class="bounce1" />
            <span class="bounce2" />
            <span class="bounce3" />
          </span>

          <template v-else>
            <i class="icon-search ml--1" />

            <span>جستجو</span>
          </template>
        </button>
      </div>

      <div v-if="isMultiCity" class="booking__tools">
        <button
          class="booking__tools-button"
          @click="handleAddRoute"
          :disabled="submitData.routes.length === 5"
        >
          <i class="icon-plus" />

          <span>اضافه کردن پرواز</span>
        </button>

        <div class="booking__tools-travelers">
          <div class="booking__travelers">
            <Travelers
              v-model="submitData.travelers"
              :cabinType.sync="submitData.cabinType"
              ref="travelers"
            />
          </div>

          <button
            @click="handleSearch"
            class="booking__submit btn btn-red"
            title="جستجو"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="bounce-loading bounce-loading--white">
              <span class="bounce1" />
              <span class="bounce2" />
              <span class="bounce3" />
            </span>

            <template v-else>
              <i class="icon-search ml--1" />

              <span>جستجو</span>
            </template>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
